import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, ChevronLeft, ChevronRight, Maximize, Minimize, LayoutGrid, EyeOff, Eye, Users, FileDown } from 'lucide-react';
import { ClientPdfDialog } from './ClientPdfDialog';
import { useAuth } from '../../contexts/AuthContext';
import { slideUrl } from './formationApi';

// Panneau de sélection des slides : masquage perso (mémorisé) + masquage pour tous (admin)
function SlidePicker({ item, token, myHidden, globalHidden, isAdmin, onToggleMine, onToggleGlobal, onGo }) {
  return (
    <div className="absolute inset-y-0 right-0 w-[340px] bg-[#1b1c1f]/95 backdrop-blur-xl border-l border-white/10 overflow-y-auto p-4 space-y-3 z-20" data-testid="slideshow-picker">
      <p className="text-white text-sm font-semibold">Slides à diffuser</p>
      <p className="text-[11px] text-white/50">Œil : masquer pour ma diffusion (mémorisé).{isAdmin ? ' Groupe : masquer pour tout le monde.' : ''}</p>
      {Array.from({ length: item.slides_count }, (_, i) => i + 1).map(n => {
        const g = globalHidden.includes(n);
        const m = myHidden.includes(n);
        return (
          <div key={n} className={`flex items-center gap-2 rounded-lg p-1.5 ${g || m ? 'opacity-45' : ''}`} data-testid={`picker-slide-${n}`}>
            <button type="button" onClick={() => onGo(n)} className="relative shrink-0">
              <img src={slideUrl(item, n, token, true)} alt={`Slide ${n}`} className="w-[150px] rounded border border-white/10" loading="lazy" />
              <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/70 text-white px-1.5 rounded">{n}</span>
            </button>
            <div className="flex flex-col gap-1.5">
              <button type="button" disabled={g} onClick={() => onToggleMine(n)} title={m ? 'Réafficher' : 'Masquer pour ma diffusion'}
                className="w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30" data-testid={`picker-mine-${n}`}>
                {m ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              {isAdmin && (
                <button type="button" onClick={() => onToggleGlobal(n)} title={g ? 'Réafficher pour tous' : 'Masquer pour tout le monde'}
                  className={`w-8 h-8 rounded-md flex items-center justify-center ${g ? 'bg-orange text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`} data-testid={`picker-global-${n}`}>
                  <Users className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SlideShow({ item, onClose, onPrefsChange }) {
  const { api, token, isAdmin } = useAuth();
  const rootRef = useRef(null);
  const [myHidden, setMyHidden] = useState(item.my_hidden || []);
  const [globalHidden, setGlobalHidden] = useState(item.hidden_global || []);
  const [pos, setPos] = useState(0);
  const [picker, setPicker] = useState(false);
  const [fs, setFs] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const visible = useMemo(() => Array.from({ length: item.slides_count }, (_, i) => i + 1)
    .filter(n => !myHidden.includes(n) && !globalHidden.includes(n)), [item.slides_count, myHidden, globalHidden]);
  const cur = visible[Math.min(pos, Math.max(visible.length - 1, 0))];
  const go = useCallback((d) => setPos(p => Math.min(Math.max(p + d, 0), Math.max(visible.length - 1, 0))), [visible.length]);
  useEffect(() => { setPos(p => Math.min(p, Math.max(visible.length - 1, 0))); }, [visible.length]);
  const toggleFs = () => (document.fullscreenElement ? document.exitFullscreen() : rootRef.current?.requestFullscreen?.());

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (pdfOpen) { if (e.key === 'Escape') setPdfOpen(false); return; }
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); go(1); }
      else if (['ArrowLeft', 'PageUp'].includes(e.key)) go(-1);
      else if (e.key === 'Escape' && !document.fullscreenElement) onClose();
      else if (e.key.toLowerCase() === 'f') toggleFs();
    };
    const onFs = () => setFs(!!document.fullscreenElement);
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFs);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('fullscreenchange', onFs); };
  }, [go, onClose, pdfOpen]);

  useEffect(() => { api().post(`/formation/items/${item.id}/open`).catch(() => {}); }, [item.id, api]);
  useEffect(() => { // suivi : dernière slide atteinte (la dernière slide diffusée compte comme terminé)
    if (!cur) return undefined;
    const t = setTimeout(() => {
      const slide = pos >= visible.length - 1 ? item.slides_count : cur;
      api().put(`/formation/items/${item.id}/progress`, { slide })
        .then(({ data }) => onPrefsChange?.(item.id, { my_progress: data.percent })).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [cur]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { // précharge la slide suivante
    const next = visible[pos + 1];
    if (next) { const im = new Image(); im.src = slideUrl(item, next, token); }
  }, [pos, visible, item.id, token]);

  const toggleMine = async (n) => {
    const nh = myHidden.includes(n) ? myHidden.filter(x => x !== n) : [...myHidden, n];
    setMyHidden(nh);
    try { await api().put(`/formation/items/${item.id}/prefs`, { hidden: nh }); onPrefsChange?.(item.id, { my_hidden: nh }); }
    catch { toast.error('Préférence non enregistrée'); }
  };
  const toggleGlobal = async (n) => {
    const nh = globalHidden.includes(n) ? globalHidden.filter(x => x !== n) : [...globalHidden, n];
    setGlobalHidden(nh);
    try { await api().put(`/formation/items/${item.id}/hidden-global`, { hidden: nh }); onPrefsChange?.(item.id, { hidden_global: nh }); }
    catch { toast.error('Masquage non enregistré'); }
  };
  const goSlide = (n) => { const i = visible.indexOf(n); if (i >= 0) setPos(i); };

  const ctrl = 'w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-20 disabled:cursor-default disabled:hover:bg-white/10';
  return (
    <div ref={rootRef} className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="slideshow">
      <div className="flex items-center gap-3 px-4 py-3 text-white/80 z-10">
        <p className="font-semibold text-white truncate flex-1" data-testid="slideshow-title">{item.titre}</p>
        <span className="text-sm tabular-nums" data-testid="slideshow-counter">{visible.length ? `${pos + 1} / ${visible.length}` : '0 / 0'}</span>
        {visible.length < item.slides_count && <span className="text-xs text-white/50">({item.slides_count - visible.length} masquée(s))</span>}
        <button onClick={() => setPdfOpen(true)} title="PDF personnalisé pour le client (slides affichées)" data-testid="slideshow-client-pdf"
          className="h-10 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold inline-flex items-center gap-2 transition-colors">
          <FileDown className="w-4 h-4" /> PDF client
        </button>
        <button className={ctrl} onClick={() => setPicker(p => !p)} title="Choisir les slides" data-testid="slideshow-picker-toggle"><LayoutGrid className="w-5 h-5" /></button>
        <button className={ctrl} onClick={toggleFs} title="Plein écran (F)" data-testid="slideshow-fullscreen">{fs ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}</button>
        <button className={ctrl} onClick={onClose} title="Fermer (Échap)" data-testid="slideshow-close"><X className="w-5 h-5" /></button>
      </div>
      <div className={`relative flex-1 flex items-center justify-center min-h-0 px-16 pb-6 transition-[padding] ${picker ? 'pr-[372px]' : ''}`}>
        {cur ? (
          <img key={cur} src={slideUrl(item, cur, token)} alt={`Slide ${cur}`} className="max-w-full max-h-full object-contain select-none animate-fade-in" draggable={false} data-testid="slideshow-image" />
        ) : <p className="text-white/60">Toutes les slides sont masquées.</p>}
        <button className={`${ctrl} absolute left-4 top-1/2 -translate-y-1/2`} onClick={() => go(-1)} disabled={pos === 0} data-testid="slideshow-prev"><ChevronLeft className="w-6 h-6" /></button>
        <button className={`${ctrl} absolute right-4 top-1/2 -translate-y-1/2`} onClick={() => go(1)} disabled={pos >= visible.length - 1} style={picker ? { right: 356 } : undefined} data-testid="slideshow-next"><ChevronRight className="w-6 h-6" /></button>
        {picker && <SlidePicker item={item} token={token} myHidden={myHidden} globalHidden={globalHidden} isAdmin={isAdmin}
          onToggleMine={toggleMine} onToggleGlobal={toggleGlobal} onGo={goSlide} />}
      </div>
      {pdfOpen && <ClientPdfDialog item={item} slides={visible} onClose={() => setPdfOpen(false)} />}
    </div>
  );
}
